import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/base/VideoCard";
import EmptyState from "@/components/base/EmptyState";
import LoadingGrid from "@/components/base/LoadingGrid";
import ErrorState from "@/components/base/ErrorState";
import { useAsyncData } from "@/hooks/useAsyncData";
import { usePageMeta } from "@/hooks/usePageMeta";
import { searchVideos, getFeaturedVideos } from "@/utils/video";
import { categories } from "@/mocks/categories";
import type { Video } from "@/types";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(q);

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const { data, loading, error, retry } = useAsyncData<Video[]>(() => searchVideos(q), [q]);

  usePageMeta(
    q ? `"${q}" 搜索结果 - 精品视频` : "搜索 - 精品视频",
    q ? `精品视频中与"${q}"相关的视频搜索结果，涵盖影视剧、综艺、原创等高清免费内容。` : "在精品视频中搜索影视剧、综艺、原创等高清免费视频。",
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    setParams(kw ? { q: kw } : {});
  };

  const results = data ?? [];
  const hasQuery = q.trim().length > 0;
  const hotVideos = getFeaturedVideos(5);

  return (
    <div className="w-full px-4 md:px-8 pt-24 md:pt-28 pb-16 md:pb-20">
      <h1 className="text-2xl md:text-3xl font-black text-foreground-950">搜索视频</h1>
      <p className="mt-1.5 text-sm text-foreground-500">输入关键词，快速找到你想看的影视剧、综艺与原创内容</p>

      <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
        <div className="flex items-center flex-1 rounded-full border border-background-200 bg-background-100 px-4 h-11">
          <i className="ri-search-line text-foreground-400" />
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="输入视频名称、类型或标签"
            className="flex-1 bg-transparent px-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 px-6 h-11 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors"
        >
          <i className="ri-search-line" />
          搜索
        </button>
      </form>

      {hasQuery && !loading && !error && (
        <p className="mt-6 text-sm text-foreground-600">
          关于「<span className="font-semibold text-primary-600">{q}</span>」共找到{" "}
          <span className="font-semibold text-primary-600">{results.length}</span> 个结果
        </p>
      )}

      <div className="mt-6">
        {!hasQuery ? (
          <div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 mb-8">
              <h2 className="text-base font-bold text-foreground-950 mb-3">热门分类</h2>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <Link
                    key={c.id}
                    to={`/category/${c.id}`}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 cursor-pointer transition-colors"
                  >
                    <i className={c.icon} />
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
            <h2 className="text-lg font-bold text-foreground-950 mb-4">大家都在看</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {hotVideos.map((v, i) => (
                <VideoCard key={v.id} video={v} index={i} />
              ))}
            </div>
          </div>
        ) : error ? (
          <ErrorState onRetry={retry} />
        ) : loading ? (
          <LoadingGrid count={5} />
        ) : results.length === 0 ? (
          <div>
            <EmptyState
              icon="ri-search-eye-line"
              title={`没有找到与「${q}」相关的视频`}
              description="换个关键词试试，或从下面的热门分类中挑选感兴趣的内容。"
            />
            <div className="max-w-lg mx-auto flex flex-wrap justify-center gap-2">
              {categories.slice(0, 4).map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium hover:bg-primary-200 cursor-pointer transition-colors"
                >
                  <i className={c.icon} />
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {results.map((v, i) => (
              <VideoCard key={v.id} video={v} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}